import axios from "axios"
import React, { Suspense, useRef, useEffect, useState } from "react"
import emailjs from '@emailjs/browser'
import * as Icon from "react-feather"
import { Helmet } from "react-helmet"
import Layout from "../components/Layout"
import Sectiontitle from "../components/Sectiontitle"
import Spinner from "../components/Spinner"

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

interface ContactInfo {
  phoneNumbers: string[]
  emailAddress: string[]
  address: string
}

const Contact: React.FC = () => {
  const form = useRef<HTMLFormElement>(null)
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([])
  const [emailAddress, setEmailAddress] = useState<string[]>([])
  const [address, setAddress] = useState("")
  const [formdata, setFormdata] = useState<FormData>({
    name: "",
    email: "",
    subject: "",
    message: ""
  })
  const [error, setError] = useState(false)
  const [message, setMessage] = useState("")

  const submitHandler = (event: React.FormEvent) => {
    event.preventDefault()
    if (!formdata.name) {
      setError(true)
      setMessage("Name is required")
    } else if (!formdata.email) {
      setError(true)
      setMessage("Email is required")
    } else if (!formdata.subject) {
      setError(true)
      setMessage("Subject is required")
    } else if (!formdata.message) {
      setError(true)
      setMessage("Message is required")
    } else {
      emailjs.sendForm('service_eywihdp', 'template_u3mfjfa', form.current!, 'pX7-ayMUgFD0inI2r')
      .then((result) => {
          setError(false)
          setMessage("You message has been sent!!!")
      }, (error) => {
          console.log(error.text)
          setError(true)
          setMessage("Message failed to send. Please email me on me@nathanhensher.dev")
      })
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormdata({
      ...formdata,
      [event.currentTarget.name]: event.currentTarget.value
    })
  }

  const numberFormatter = (number: string): string => {
    return number
  }

  const handleAlerts = () => {
    if (error && message) {
      return <div className="alert alert-danger mt-4">{message}</div>
    } else if (!error && message) {
      return <div className="alert alert-success mt-4">{message}</div>
    } else {
      return null
    }
  }

  useEffect(() => {
    axios.get<ContactInfo>("/api/contactinfo").then((response) => {
      setPhoneNumbers(response.data.phoneNumbers)
      setEmailAddress(response.data.emailAddress)
      setAddress(response.data.address)
    })
  }, [])

  return (
    <Layout>
      <Helmet>
        <title>Contact - Nathan Hensher</title>
        <meta
          name="description"
          content="Nathan Hensher - Javascript Engineer - Contact Page"
        />
      </Helmet>
      <Suspense fallback={<Spinner />}>
        <div className="mi-contact-area mi-section mi-padding-top mi-padding-bottom">
          <div className="container">
            <Sectiontitle title="Contact Me" />
            <div className="row">
              <div className="col-lg-6">
                <div className="mi-contact-formwrapper">
                  <h4>Get In Touch</h4>
                  <form
                    ref={form}
                    className="mi-form mi-contact-form"
                    onSubmit={submitHandler}
                  >
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-name">
                        Enter your name*
                      </label>
                      <input
                        onChange={handleChange}
                        type="text"
                        name="name"
                        id="contact-form-name"
                        value={formdata.name}
                      />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-email">
                        Enter your email*
                      </label>
                      <input
                        onChange={handleChange}
                        type="text"
                        name="email"
                        id="contact-form-email"
                        value={formdata.email}
                      />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-subject">
                        Enter your subject*
                      </label>
                      <input
                        onChange={handleChange}
                        type="text"
                        name="subject"
                        id="contact-form-subject"
                        value={formdata.subject}
                      />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-message">
                        Enter your Message*
                      </label>
                      <textarea
                        onChange={handleChange}
                        name="message"
                        id="contact-form-message"
                        cols={30}
                        rows={6}
                        value={formdata.message}
                      ></textarea>
                    </div>
                    <div className="mi-form-field">
                      <button className="mi-button" type="submit">
                        Send
                      </button>
                    </div>
                  </form>
                  {handleAlerts()}
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mi-contact-info">
                  {!phoneNumbers ? null : (
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon">
                        <Icon.Phone />
                      </span>
                      <div className="mi-contact-infoblock-content">
                        <h6>Phone</h6>
                        {phoneNumbers.map((phoneNumber) => (
                          <p key={phoneNumber}>
                            <a href={numberFormatter(phoneNumber)}>
                              {phoneNumber}
                            </a>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {!emailAddress ? null : (
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon">
                        <Icon.Mail />
                      </span>
                      <div className="mi-contact-infoblock-content">
                        <h6>Email</h6>
                        {emailAddress.map((email) => (
                          <p key={email}>
                            <a href={`mailto:${email}`}>{email}</a>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {!phoneNumbers ? null : (
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon">
                        <Icon.MapPin />
                      </span>
                      <div className="mi-contact-infoblock-content">
                        <h6>Address</h6>
                        <p>{address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Suspense>
    </Layout>
  )
}

export default Contact
